import { useEffect, useState } from "react";

import { TextField, RadioButton } from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models/Common/CodeBook";
import { hrDate, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import {
  getSchoolCandidateById,
  getSchoolCandidateRemark,
  saveSchoolCandidateInstitutionCountyAssignment,
} from "../../api/SchoolCandidate/SchoolCandidateApi";
import { useTypedSelector } from "../../app/rootReducer";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  CountySelect,
  FormCard,
  InstitutionSelect,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { useAuthCounties } from "../../hooks";
import { GenderEnum, ValidationFailure } from "../../models";
import {
  SchoolCandidate,
  UpdateSchoolCandidateInstitutionCountyAssignment,
  SchoolCandidateInstitutionChangeType,
  SchoolCandidateStatus,
  getDefaultSchoolCandidate,
  SchoolCandidateRemarkType,
  SchoolCandidateRemark,
} from "../../models/SchoolCandidate";
import { useGetInstitutionListForUserQuery } from "../../queries/codeBookQuery";
import {
  schoolCandidateDetailUrl,
  schoolCandidateRoute,
} from "../../routes/routes";
import {
  resetIsEdited,
  resetRedirectURL,
  setIsEdited,
  setSaveOK,
} from "../../slices/applicationSlice";
import { FormatUtils, MsgUtils } from "../../utils";
import { SchoolCandidateRemarks } from "./components";

export function SchoolCandidateInstitutionReassignment() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const authCounties = useAuthCounties();

  const defaultSchoolCandidate = getDefaultSchoolCandidate();
  const createDefaultUpdate =
    (): UpdateSchoolCandidateInstitutionCountyAssignment => {
      return {
        schoolCandidateId: +id,
        changeType: SchoolCandidateInstitutionChangeType.Confirmation,
      };
    };
  const [item, setItem] = useState<SchoolCandidate>(defaultSchoolCandidate);
  const { data: institutions } = useGetInstitutionListForUserQuery();
  const [genders] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(GenderEnum, "gender")
  );
  const [statuses] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(SchoolCandidateStatus, "schoolCandidateStatus")
  );
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [update, setUpdate] =
    useState<UpdateSchoolCandidateInstitutionCountyAssignment>(
      createDefaultUpdate()
    );
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [remarks, setRemarks] = useState<SchoolCandidateRemark[]>();

  const redirectURL = useTypedSelector(
    (state) => state.application.redirectURL
  );

  useEffect(() => {
    (async () => {
      const result = await getSchoolCandidateById(+id);
      if (result) {
        setItem(result);
        if (result.schoolYear && result.schoolYear.isCurrent) {
          setIsEditable(true);
          dispatch(setIsEdited());
        }
      }

      const remarkResult = await getSchoolCandidateRemark(
        +id,
        SchoolCandidateRemarkType.CountyVerificationRemark
      );

      if (!!remarkResult.length) {
        setRemarks(remarkResult);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChangeTypeChange = (e: any) => {
    const changeType = Number.parseInt(e.target.value);

    const newUpdate: UpdateSchoolCandidateInstitutionCountyAssignment = {
      ...update,
      changeType: changeType,
    };
    switch (changeType) {
      case SchoolCandidateInstitutionChangeType.Confirmation:
        newUpdate.institutionId = undefined;
        newUpdate.uoguCountyId = undefined;
        break;
      case SchoolCandidateInstitutionChangeType.InstitutionChange:
        newUpdate.uoguCountyId = undefined;
        break;
      case SchoolCandidateInstitutionChangeType.CountyChange:
        newUpdate.institutionId = undefined;
        break;
    }
    setUpdate(newUpdate);
    dispatch(setIsEdited());
  };
  const handleInstitutionChange = (e: any) => {
    const institutionId = Number.parseInt(e.target.value);
    if (!isNaN(institutionId)) {
      const newUpdate: UpdateSchoolCandidateInstitutionCountyAssignment = {
        ...update,
        institutionId: institutionId,
      };
      setUpdate(newUpdate);
      dispatch(setIsEdited());
    }
  };
  const handleCountyChange = (e: any) => {
    const countyId = Number.parseInt(e.target.value);
    const newUpdate: UpdateSchoolCandidateInstitutionCountyAssignment = {
      ...update,
      institutionId: undefined,
      uoguCountyId: !isNaN(countyId) ? countyId : undefined,
    };
    setUpdate(newUpdate);
    dispatch(setIsEdited());
  };
  const handleSaveSchoolCandidate = () => {
    saveSchoolCandidateInstitutionCountyAssignment(update).then((result) => {
      if (result && result.result !== undefined) {
        if (result.isValid) {
          dispatch(resetIsEdited());
          dispatch(setSaveOK());
          if (
            update?.uoguCountyId &&
            authCounties.find((x) => x.id === update?.uoguCountyId)
          )
            history.replace(
              schoolCandidateDetailUrl(update?.schoolCandidateId)
            );
          else {
            if (redirectURL) {
              dispatch(resetRedirectURL());
              history.push(redirectURL);
            } else {
              history.replace(schoolCandidateRoute);
            }
          }
        }
        setErrors(result.validationFailures);
      }
    });
  };

  return (
    <ContentArea>
      <ToolbarTop
        pageTitle={t("common.schoolCandidateModel.institutionReassignPageName")}
        showBack={"show"}
        errors={errors}
      />
      <FormCard
        cardTitle={t(
          "common.schoolCandidateModel.schoolCandidatePersonalDetails"
        )}
        labelStatus={{
          color: FormatUtils.getSchoolCandidateStatusColor(item.statusId),
          name: statuses.find((v) => v.id === item.statusId)?.name ?? "",
        }}
      >
        <Row>
          <Col md={12}>
            <TextField
              label={t("common.schoolCandidateModel.oib")}
              value={item.oib}
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextField
              label={t("common.schoolCandidateModel.alternativeCode")}
              value={item.alternativeCode ?? ""}
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <TextField
              label={t("common.schoolCandidateModel.lastName")}
              value={item.lastName}
              readonly
              plaintext
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t("common.schoolCandidateModel.firstName")}
              value={item.firstName}
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <TextField
              label={t("common.schoolCandidateModel.dateOfBirth")}
              value={hrDate(new Date(item.dateOfBirth))}
              readonly
              plaintext
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t("common.schoolCandidateModel.schoolCandidateGender")}
              value={genders.find((v) => v.id === item.genderId)?.name}
              readonly
              plaintext
            />
          </Col>
        </Row>
      </FormCard>
      <FormCard cardTitle={t("common.schoolCandidateModel.permanentAddress")}>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.countyList")}
              value={item.residence?.permanentAddress?.county}
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.municipalityList")}
              value={
                item.residence?.permanentAddress?.municipality ??
                item.residence?.address ??
                ""
              }
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.townList")}
              value={item.residence?.permanentAddress?.town}
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.streetList")}
              value={item.residence?.permanentAddress?.street}
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.houseNumberSelector")}
              value={item.residence?.permanentAddress?.houseNumber ?? undefined}
              readonly
              plaintext
            />
          </Col>
          <Col md={4}></Col>
        </Row>
      </FormCard>
      <FormCard cardTitle={t("common.schoolCandidateModel.temporaryAddress")}>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.countyList")}
              value={
                item.residence?.temporaryAddress
                  ? item.residence?.temporaryAddress.county
                  : ""
              }
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.municipalityList")}
              value={
                item.residence?.temporaryAddress
                  ? item.residence?.temporaryAddress.municipality
                  : ""
              }
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.townList")}
              value={
                item.residence?.temporaryAddress
                  ? item.residence?.temporaryAddress.town
                  : ""
              }
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.streetList")}
              value={
                item.residence?.temporaryAddress
                  ? item.residence?.temporaryAddress.street
                  : ""
              }
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.houseNumberSelector")}
              value={
                item.residence?.temporaryAddress
                  ? item.residence?.temporaryAddress.houseNumber ?? undefined
                  : ""
              }
              readonly
              plaintext
            />
          </Col>
          <Col md={4}></Col>
        </Row>
      </FormCard>
      <FormCard>
        <Row>
          <Col md={12}>
            <TextField
              label={t("common.schoolCandidateModel.institutionList")}
              value={
                institutions?.find((v) => v.id === item.institutionId)?.name
              }
              readonly
              plaintext
            />
          </Col>
        </Row>
      </FormCard>
      <SchoolCandidateRemarks remarks={remarks} />
      <FormCard>
        <Row>
          <Col md={6} className="text-right">
            <RadioButton
              label={t("schoolCandidateInstitutionChangeType.Confirmation")}
              ariaLabel="confirmation"
              key="confirmation"
              name="confirmation"
              checked={
                update.changeType ===
                SchoolCandidateInstitutionChangeType.Confirmation
              }
              onChange={handleChangeTypeChange}
              value={SchoolCandidateInstitutionChangeType.Confirmation}
              disabled={!isEditable}
            />
          </Col>
        </Row>
        <hr />
        <Row>
          <Col md={6}>
            <RadioButton
              label={t(
                "schoolCandidateInstitutionChangeType.InstitutionChange",
                {
                  uoguCountyName: authCounties.find(
                    (v) => v.id === item?.uoguCountyId
                  )?.name,
                }
              )}
              ariaLabel="institutionChange"
              key="institutionChange"
              name="institutionChange"
              checked={
                update.changeType ===
                SchoolCandidateInstitutionChangeType.InstitutionChange
              }
              onChange={handleChangeTypeChange}
              value={SchoolCandidateInstitutionChangeType.InstitutionChange}
              disabled={!isEditable}
            />
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={nameof<typeof update>("institutionId")}
              value={update.institutionId}
              onlyByEnrollmentArea={true}
              onChange={handleInstitutionChange}
              countyId={item?.uoguCountyId}
              excludeIds={item.institutionId ? [item.institutionId] : undefined}
              disabled={
                !isEditable ||
                update.changeType !==
                  SchoolCandidateInstitutionChangeType.InstitutionChange
              }
              errorText={
                errors?.find((e) => e.propertyName === "InstitutionId")?.message
              }
              showUser={false}
            />
          </Col>
        </Row>
        <hr />
        <Row>
          <Col md={6}>
            <RadioButton
              label={t("schoolCandidateInstitutionChangeType.CountyChange")}
              ariaLabel="countyChange"
              key="countyChange"
              name="countyChange"
              checked={
                update.changeType ===
                SchoolCandidateInstitutionChangeType.CountyChange
              }
              onChange={handleChangeTypeChange}
              value={SchoolCandidateInstitutionChangeType.CountyChange}
              disabled={!isEditable}
            />
            <CountySelect
              label={t("common.schoolCandidateModel.uoguCounty")}
              name={"schoolCandidate.uoguCounty"}
              value={update.uoguCountyId}
              allowEmpty={
                update.changeType !==
                SchoolCandidateInstitutionChangeType.CountyChange
              }
              excludeCountyIds={[item.uoguCountyId]}
              onChange={handleCountyChange}
              disabled={
                !isEditable ||
                update.changeType !==
                  SchoolCandidateInstitutionChangeType.CountyChange
              }
              errorText={
                errors?.find((e) => e.propertyName === "UOGUCountyId")?.message
              }
            />
          </Col>
        </Row>
      </FormCard>
      <ToolbarEdit
        showSave={"show"}
        showCancel={"show"}
        onClickSave={handleSaveSchoolCandidate}
        setSaveErrors={setErrors}
      />
      <Row>
        <Col>&nbsp;</Col>
      </Row>
      <Row>
        <Col>&nbsp;</Col>
      </Row>
      <Row>
        <Col>&nbsp;</Col>
      </Row>
      <Row>
        <Col>&nbsp;</Col>
      </Row>
      <Row>
        <Col>&nbsp;</Col>
      </Row>
      <Row>
        <Col>&nbsp;</Col>
      </Row>
      <Row>
        <Col>&nbsp;</Col>
      </Row>
    </ContentArea>
  );
}
